/**
 * ErrorBoundary — React render-error catch.
 *
 * Wraps the app root so a render-time throw from any component does NOT
 * blank the entire page. Instead the editor sees a visible fallback UI
 * with the error message + stack, plus a Retry button. The error is also
 * logged to admin_errors via the errorLogger module so we can review it
 * later without depending on the user copy-pasting from DevTools.
 *
 * This is the safety net for the failure mode that's been biting us
 * repeatedly — a single unmapped tier or undefined-property access in a
 * deeply nested component throws, React unmounts the entire tree, and
 * the page renders nothing. With this boundary in place that scenario
 * shows the error inline and the editor can refresh / file a bug instead
 * of staring at a white screen.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { logError } from '../lib/errorLogger';

interface Props {
  children: ReactNode;
  /** Optional custom fallback. Receives the caught error and a retry callback. */
  fallback?: (error: Error, retry: () => void) => ReactNode;
  /** Human-readable boundary name — surfaces in the admin_errors meta column. */
  boundaryName?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Auto-recover from "Failed to fetch dynamically imported module" — the
    // classic stale-SPA-chunk error after a fresh deploy. Pattern: editor
    // had an old index.html loaded with references to old chunk hashes;
    // CF Pages shipped a new build with different chunk hashes; lazy-import
    // of a route component 404s because the old hash no longer exists on
    // disk. The cure is to refetch index.html (which now points at the new
    // chunks), which is exactly what a full window.location.reload does.
    //
    // We trigger the reload after a short delay so logError still has time
    // to ship and so the editor briefly sees the error UI flash (signal
    // that *something* happened, even though we're handling it). One
    // reload-loop guard via sessionStorage so a hard render bug doesn't
    // spin forever — we attempt auto-reload at most once per session.
    const isChunkLoadError =
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Importing a module script failed');
    const alreadyAutoReloaded = sessionStorage.getItem('chunkReloadAttempted') === '1';
    if (isChunkLoadError && !alreadyAutoReloaded) {
      sessionStorage.setItem('chunkReloadAttempted', '1');
      console.warn('[ErrorBoundary] stale-chunk error detected — auto-reloading after deploy');
      // Logged as react_error with recovery_action in meta — keeps the
      // ErrorType union + admin_errors CHECK constraint in sync. The
      // earlier code used a 6th error_type value 'chunk_load_auto_reload'
      // that wasn't in either the TS union or the DB CHECK constraint —
      // tsc -b in CF Pages caught the union mismatch (TS2322) and silently
      // blocked every admin deploy from e994036 onward. Filter recovered
      // events in the admin_errors view by meta->>'recovery_action'.
      void logError({
        error_type: 'react_error',
        message:    error.message,
        stack:      error.stack ?? null,
        meta: {
          boundary_name:    this.props.boundaryName ?? 'root',
          component_stack:  errorInfo.componentStack,
          recovery_action:  'chunk_load_auto_reload',
        },
      });
      setTimeout(() => { window.location.reload(); }, 800);
      return;
    }

    void logError({
      error_type: 'react_error',
      message:    error.message,
      stack:      error.stack ?? null,
      meta: {
        boundary_name:   this.props.boundaryName ?? 'root',
        component_stack: errorInfo.componentStack,
      },
    });
    // Also surface in the browser console so dev sees it immediately
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, errorInfo);
  }

  retry = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.retry);
      }
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-red-50">
          <div className="max-w-3xl w-full bg-white border border-red-200 rounded-lg p-6 space-y-4 shadow-sm">
            <div>
              <h1 className="text-xl font-bold text-red-700">Admin hit a render error</h1>
              <p className="text-sm text-gray-700 mt-1">
                The page caught a runtime exception while rendering. It has been logged to{' '}
                <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">admin_errors</code> for review.
                You can retry, refresh the page, or check the error log to see what failed.
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-3 font-mono text-xs overflow-auto max-h-96">
              <div className="font-semibold text-red-700">{this.state.error.message}</div>
              {this.state.error.stack && (
                <pre className="mt-2 whitespace-pre-wrap text-gray-700 leading-relaxed">{this.state.error.stack}</pre>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={this.retry}
                className="px-4 py-2 bg-brand-orange text-white rounded-md text-sm font-semibold hover:opacity-90"
              >
                Try again
              </button>
              <a
                href="/prototype/admin-errors"
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 inline-flex items-center"
              >
                View error log
              </a>
              <a
                href="/prototype/dashboard"
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 inline-flex items-center"
              >
                Back to dashboard
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
